"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import { BlogCard } from "@/app/components/blog/BlogCard";
import { BlogSidebar } from "@/app/components/blog/BlogSidebar";
import {
  fetchBlogPosts,
  fetchBlogCategorySummary,
  fetchPopularPosts,
  getPopularPosts,
} from "@/app/services/blogApi";
import { BlogPost, BlogCategory } from "@/app/types/blog";
import NavbarSection from "@/app/components/NavbarSection";
import FooterSection from "@/app/components/FooterSection";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import MakeWebsiteLiveSection from "@/app/components/blog/MakeWebsiteLiveSection";
import { Pagination } from "@/app/components/blog/Pagination";

interface CategoryClientProps {
  slug: string;
}

const POSTS_PER_PAGE = 10;

const CategoryClient: React.FC<CategoryClientProps> = ({ slug }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryTitle, setCategoryTitle] = useState<string>("All Posts");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [popular, setPopular] = useState<BlogPost[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const decodedCategoryParam = useMemo(
    () =>
      decodeURIComponent(slug.replace(/\+/g, " ")).trim(),
    [slug]
  );

  const decodeHtmlEntities = (value: string): string =>
    value
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&ldquo;/g, "“")
      .replace(/&rdquo;/g, "”")
      .replace(/&lsquo;/g, "‘")
      .replace(/&rsquo;/g, "’");

  useEffect(() => {
    setCurrentPage(1);
  }, [decodedCategoryParam]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        if (!isMounted) return;
        setLoading(true);

        const categorySummary = await fetchBlogCategorySummary();
        if (!isMounted) return;

        setCategories(categorySummary.categories);

        const matchedCategory = categorySummary.categories.find(
          (category) =>
            category.slug?.toLowerCase() === decodedCategoryParam.toLowerCase()
        );

        const derivedName =
          matchedCategory?.name ??
          (decodeHtmlEntities(
            decodedCategoryParam.replace(/-/g, " ")
          ) || "All Posts");
        if (!isMounted) return;
        setCategoryTitle(derivedName);

        const [postsResult, popularResult] = await Promise.all([
          fetchBlogPosts({
            page: currentPage,
            perPage: POSTS_PER_PAGE,
            category: matchedCategory?.slug ?? decodedCategoryParam,
          }),
          fetchPopularPosts(5),
        ]);

        if (!isMounted) return;

        let fetchedPosts = postsResult.posts;

        if (!fetchedPosts.length && !matchedCategory && currentPage === 1) {
          const fallback = await fetchBlogPosts({ perPage: 100 });
          if (!isMounted) return;
          fetchedPosts = fallback.posts.filter(
            (post) => post.category.toLowerCase() === derivedName.toLowerCase()
          );
        }

        const categoryTotal =
          Number(matchedCategory?.count) ||
          postsResult.total ||
          fetchedPosts.length;

        const resolvedTotalPages =
          postsResult.totalPages ??
          Math.max(1, Math.ceil(categoryTotal / POSTS_PER_PAGE));

        if (currentPage > resolvedTotalPages) {
          setTotalPages(resolvedTotalPages);
          setCurrentPage(1);
          return;
        }

        setPosts(fetchedPosts);
        const globalTotal =
          Number(categorySummary.total) ||
          postsResult.total ||
          fetchedPosts.length;
        setTotalCount(globalTotal);
        setTotalPages(resolvedTotalPages);

        setPopular(
          popularResult.length
            ? popularResult
            : getPopularPosts(fetchedPosts, 5)
        );
      } catch (error) {
        console.error("Failed to load category data:", error);
        setPosts([]);
        setPopular([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [decodedCategoryParam, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading)
    return (
      <>
        <NavbarSection />
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
        <FooterSection />
      </>
    );

  return (
    <>
      <NavbarSection />
      <Container className="py-5">
        <Link href="/blog" className="btn text-decoration-none mb-4 p-0 goBack">
          <ArrowLeft size={16} className="me-2" />
          Back to Blog
        </Link>
        <h2 className="mb-4 text-capitalize">{categoryTitle}</h2>

        <Row>
          {/* Left: Blog Cards */}
          <Col md={8}>
            <Row className="g-4">
              {posts.map((p, i) => (
                <Col key={p.id} md={6} sm={12}>
                  <BlogCard post={p} index={i} />
                </Col>
              ))}
            </Row>

            {totalPages > 1 && (
              <div className="mt-5">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </Col>

          {/* Right: Sidebar */}
          <Col md={4}>
            <BlogSidebar
              categories={categories}
              popularPosts={popular}
              selectedCategory={categoryTitle}
              onCategorySelect={() => setCurrentPage(1)}
              totalCount={totalCount}
            />
          </Col>
        </Row>

        <MakeWebsiteLiveSection />
      </Container>
      <FooterSection />
    </>
  );
};

export default CategoryClient;
